import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Activity, 
  Database, 
  Server, 
  HardDrive, 
  Cpu, 
  MemoryStick,
  Clock,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Wifi
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import ApiService from '../services/api';
import { useNotifications } from '../context/NotificationContext';

interface SystemHealthData {
  api_status: 'healthy' | 'degraded' | 'down';
  database_status: 'connected' | 'slow' | 'disconnected';
  storage_usage: number; // percentage
  response_times: Array<{ timestamp: string; time: number }>;
  active_processes: number;
  memory_usage: number; // percentage
  cpu_usage: number; // percentage
  uptime: number; // hours
  total_requests: number;
  error_rate: number; // percentage
  gemini_api_status: 'active' | 'rate_limited' | 'error';
  yolo_model_status: 'loaded' | 'loading' | 'error';
}

const SystemHealth: React.FC = () => {
  const [healthData, setHealthData] = useState<SystemHealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const { addToast, addNotification } = useNotifications();

  // Generate mock data for demonstration
  const generateMockHealthData = (): SystemHealthData => {
    const now = Date.now();
    const responseTimeData = Array.from({ length: 12 }, (_, i) => ({
      timestamp: new Date(now - (11 - i) * 5 * 60000).toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' }),
      time: Math.random() * 500 + 100
    }));

    const cpuUsage = Math.random() * 60 + 20;
    const memoryUsage = Math.random() * 70 + 15;
    const storageUsage = Math.random() * 40 + 30;
    const errorRate = Math.random() * 2;

    return {
      api_status: errorRate > 1.5 ? 'degraded' : 'healthy',
      database_status: Math.random() > 0.1 ? 'connected' : 'slow',
      storage_usage: storageUsage,
      response_times: responseTimeData,
      active_processes: Math.floor(Math.random() * 10) + 3,
      memory_usage: memoryUsage,
      cpu_usage: cpuUsage,
      uptime: Math.floor(Math.random() * 200) + 50,
      total_requests: Math.floor(Math.random() * 10000) + 5000,
      error_rate: errorRate,
      gemini_api_status: Math.random() > 0.1 ? 'active' : 'rate_limited',
      yolo_model_status: Math.random() > 0.05 ? 'loaded' : 'loading'
    };
  };

  const loadHealthData = async () => {
    try {
      setLoading(true);
      
      // Try to get real API health data
      const apiHealth = await ApiService.getDetailedHealth();
      
      // For now, combine with mock data since detailed system metrics aren't available
      const mockData = generateMockHealthData();
      
      // Check if we should alert about any issues
      checkForAlerts(mockData);
      
      setHealthData(mockData);
      
    } catch (error) {
      console.error('Failed to load system health:', error);
      
      // Use mock data if API fails
      const mockData = generateMockHealthData();
      mockData.api_status = 'down';
      setHealthData(mockData);
      
      addToast({
        title: 'تحذير النظام',
        message: 'فشل في تحميل بيانات صحة النظام',
        type: 'warning',
      });
    } finally {
      setLoading(false);
    }
  };

  const checkForAlerts = (data: SystemHealthData) => {
    // CPU usage alert
    if (data.cpu_usage > 80) {
      addNotification({
        title: 'تحذير: استخدام عالي للمعالج',
        message: `استخدام المعالج وصل إلى ${Math.round(data.cpu_usage)}%`,
        type: 'warning',
      });
    }

    // Memory usage alert
    if (data.memory_usage > 85) {
      addNotification({
        title: 'تحذير: استخدام عالي للذاكرة',
        message: `استخدام الذاكرة وصل إلى ${Math.round(data.memory_usage)}%`,
        type: 'warning',
      });
    }

    // Storage usage alert
    if (data.storage_usage > 90) {
      addNotification({
        title: 'تحذير: مساحة التخزين ممتلئة',
        message: `مساحة التخزين وصلت إلى ${Math.round(data.storage_usage)}%`,
        type: 'error',
      });
    }

    // Error rate alert
    if (data.error_rate > 2) {
      addNotification({
        title: 'تحذير: معدل خطأ عالي',
        message: `معدل الأخطاء وصل إلى ${data.error_rate.toFixed(1)}%`,
        type: 'error',
      });
    }
  };

  useEffect(() => {
    loadHealthData();
    
    if (autoRefresh) {
      const interval = setInterval(loadHealthData, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'connected':
      case 'active':
      case 'loaded':
        return 'text-success-600 bg-success-100';
      case 'degraded':
      case 'slow':
      case 'rate_limited':
      case 'loading':
        return 'text-warning-600 bg-warning-100';
      case 'down':
      case 'disconnected':
      case 'error':
        return 'text-error-600 bg-error-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status: string) => {
    const statusTexts: { [key: string]: string } = {
      healthy: 'سليم',
      degraded: 'متدهور',
      down: 'معطل',
      connected: 'متصل',
      slow: 'بطيء',
      disconnected: 'منقطع',
      active: 'نشط',
      rate_limited: 'محدود',
      error: 'خطأ',
      loaded: 'محمّل',
      loading: 'جاري التحميل'
    };
    return statusTexts[status] || status;
  };

  const getUsageColor = (percentage: number) => {
    if (percentage > 80) return 'text-error-600';
    if (percentage > 60) return 'text-warning-600';
    return 'text-success-600';
  };

  if (loading) {
    return (
      <div className="card-gradient">
        <div className="card-header">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary-600" />
            صحة النظام
          </h3>
        </div>
        <div className="card-body">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="grid grid-cols-3 gap-4">
              <div className="h-16 bg-gray-200 rounded"></div>
              <div className="h-16 bg-gray-200 rounded"></div>
              <div className="h-16 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!healthData) {
    return (
      <div className="card-gradient">
        <div className="card-header">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <Activity className="w-5 h-5 text-error-600" />
            صحة النظام - خطأ
          </h3>
        </div>
        <div className="card-body">
          <div className="text-center py-8">
            <AlertTriangle className="w-12 h-12 text-error-500 mx-auto mb-3" />
            <p className="text-error-600">فشل في تحميل بيانات صحة النظام</p>
            <button onClick={loadHealthData} className="btn-primary mt-4">
              <RefreshCw className="w-4 h-4 ml-2" />
              إعادة المحاولة
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* System Status Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card-gradient"
      >
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary-600" />
              صحة النظام
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`text-xs px-3 py-1 rounded-lg transition-colors ${
                  autoRefresh ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {autoRefresh ? 'تحديث تلقائي' : 'تحديث يدوي'}
              </button>
              <button onClick={loadHealthData} className="btn-outline text-xs">
                <RefreshCw className="w-3 h-3 ml-1" />
                تحديث
              </button>
            </div>
          </div>
        </div>
        <div className="card-body">
          {/* Status Indicators */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="stat-card-mini">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Server className="w-4 h-4 text-primary-600" />
                  <span className="text-sm font-medium">API</span>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(healthData.api_status)}`}>
                  {getStatusText(healthData.api_status)}
                </span>
              </div>
            </div>

            <div className="stat-card-mini">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Database className="w-4 h-4 text-secondary-600" />
                  <span className="text-sm font-medium">قاعدة البيانات</span>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(healthData.database_status)}`}>
                  {getStatusText(healthData.database_status)}
                </span>
              </div>
            </div>

            <div className="stat-card-mini">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Wifi className="w-4 h-4 text-indigo-600" />
                  <span className="text-sm font-medium">Gemini API</span>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(healthData.gemini_api_status)}`}>
                  {getStatusText(healthData.gemini_api_status)}
                </span>
              </div>
            </div>

            <div className="stat-card-mini">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium">YOLO</span>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(healthData.yolo_model_status)}`}>
                  {getStatusText(healthData.yolo_model_status)}
                </span>
              </div>
            </div>
          </div>

          {/* Resource Usage */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Cpu className="w-4 h-4 text-primary-600" />
                  <span className="text-sm font-medium">المعالج</span>
                </div>
                <span className={`text-sm font-bold ${getUsageColor(healthData.cpu_usage)}`}>
                  {Math.round(healthData.cpu_usage)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${
                    healthData.cpu_usage > 80 ? 'bg-error-500' :
                    healthData.cpu_usage > 60 ? 'bg-warning-500' : 'bg-success-500'
                  }`}
                  style={{ width: `${healthData.cpu_usage}%` }}
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MemoryStick className="w-4 h-4 text-secondary-600" />
                  <span className="text-sm font-medium">الذاكرة</span>
                </div>
                <span className={`text-sm font-bold ${getUsageColor(healthData.memory_usage)}`}>
                  {Math.round(healthData.memory_usage)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${
                    healthData.memory_usage > 80 ? 'bg-error-500' :
                    healthData.memory_usage > 60 ? 'bg-warning-500' : 'bg-success-500'
                  }`}
                  style={{ width: `${healthData.memory_usage}%` }}
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <HardDrive className="w-4 h-4 text-indigo-600" />
                  <span className="text-sm font-medium">التخزين</span>
                </div>
                <span className={`text-sm font-bold ${getUsageColor(healthData.storage_usage)}`}>
                  {Math.round(healthData.storage_usage)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${
                    healthData.storage_usage > 80 ? 'bg-error-500' :
                    healthData.storage_usage > 60 ? 'bg-warning-500' : 'bg-success-500'
                  }`}
                  style={{ width: `${healthData.storage_usage}%` }}
                />
              </div>
            </div>
          </div>

          {/* Response Time Chart */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary-600" />
              زمن الاستجابة (آخر ساعة)
            </h4>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={healthData.response_times}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis 
                    dataKey="timestamp" 
                    tick={{ fontSize: 11 }}
                    tickMargin={10}
                  />
                  <YAxis 
                    tick={{ fontSize: 11 }}
                    label={{ value: 'ميللي ثانية', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                    formatter={(value: number) => [`${value.toFixed(0)} مث`, 'زمن الاستجابة']}
                  />
                  <Area
                    type="monotone"
                    dataKey="time"
                    stroke="#3b82f6"
                    fill="#3b82f6"
                    fillOpacity={0.3}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </motion.div>

      {/* System Statistics */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-2 lg:grid-cols-4 gap-4"
      >
        <div className="stat-card-mini">
          <div className="text-center">
            <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-xl mx-auto mb-2">
              <TrendingUp className="w-5 h-5 text-primary-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900">{healthData.uptime}h</h3>
            <p className="text-sm text-gray-600">وقت التشغيل</p>
          </div>
        </div>

        <div className="stat-card-mini">
          <div className="text-center">
            <div className="flex items-center justify-center w-10 h-10 bg-secondary-100 rounded-xl mx-auto mb-2">
              <Activity className="w-5 h-5 text-secondary-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900">{healthData.active_processes}</h3>
            <p className="text-sm text-gray-600">العمليات النشطة</p>
          </div>
        </div>

        <div className="stat-card-mini">
          <div className="text-center">
            <div className="flex items-center justify-center w-10 h-10 bg-indigo-100 rounded-xl mx-auto mb-2">
              <Server className="w-5 h-5 text-indigo-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900">{healthData.total_requests.toLocaleString()}</h3>
            <p className="text-sm text-gray-600">إجمالي الطلبات</p>
          </div>
        </div>

        <div className="stat-card-mini">
          <div className="text-center">
            <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-xl mx-auto mb-2">
              <CheckCircle className="w-5 h-5 text-purple-600" />
            </div>
            <h3 className={`text-2xl font-bold ${getUsageColor(healthData.error_rate * 10)}`}>
              {healthData.error_rate.toFixed(1)}%
            </h3>
            <p className="text-sm text-gray-600">معدل الخطأ</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SystemHealth;